import type { Num, Recent, Source } from './types'

export const DASH = '–'
export const n = (v: Num | undefined) => (typeof v === 'number' ? Math.round(v).toLocaleString('en-US') : DASH)
export const plural = (c: number, one: string, many = `${one}s`) => `${c.toLocaleString('en-US')} ${c === 1 ? one : many}`
export const pct = (v: Num | undefined) => (typeof v === 'number' ? `${(v * 100).toFixed(v < 0.1 ? 1 : 0)}%` : DASH)
export const secs = (v: Num | undefined) => (typeof v === 'number' ? (v >= 60 ? `${Math.floor(v / 60)}m ${Math.round(v % 60)}s` : `${Math.round(v)}s`) : DASH)
export const sum = (vs: Num[]): Num => (vs.some((v) => typeof v === 'number') ? vs.reduce<number>((a, v) => a + (v ?? 0), 0) : null)

// A date-only value is a UTC day; a timestamp shows in the viewer's own time zone.
const dayOnly = (iso: string) => iso.length === 10
const at = (iso: string) => new Date(dayOnly(iso) ? `${iso}T12:00:00Z` : iso)
const local = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const today = (now = new Date()) => local(now)
export const yesterday = (now = new Date()) => local(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))
export const dayOf = (iso: string) => (dayOnly(iso) ? iso : local(new Date(iso)))
export const isToday = (iso: string | null | undefined, now = new Date()) => !!iso && dayOf(iso) === today(now)
export const short = (iso: string | null | undefined) => (iso ? at(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : DASH)
export const weekday = (iso: string) => at(iso).toLocaleDateString('en-US', { weekday: 'long' })
export const clock = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
export const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : DASH
export const headerDate = (now = new Date()) => now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '')

/** "10:58 AM", "yesterday" or "Oct 28": when a source was last read. */
export function readAt(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return 'never'
  if (dayOf(iso) === today(now)) return dayOnly(iso) ? 'today' : clock(iso)
  if (dayOf(iso) === yesterday(now)) return 'yesterday'
  return short(iso)
}

/** One clause for the header: "Emails read 10:58 AM", "bookings yesterday". */
export function sourceClause(s: Source, first: boolean): string {
  const name = first ? `${s.label} read` : s.id === 'crm' ? s.label : s.label.toLowerCase()
  if (s.state === 'missing') return `${name} not read`
  return s.run_failed ? `${name} failed, last read ${readAt(s.as_of)}` : `${name} ${readAt(s.as_of)}`
}

/** Read inside its window by a run that worked. */
export const current = (s: Source | undefined) => s?.state === 'ok' && !s.run_failed

export function sourceText(s: Source): string {
  const read = readAt(s.as_of)
  if (s.state === 'missing') return s.as_of ? `Not read, last ${read}` : 'Not read'
  if (s.run_failed) return `Last run failed, last read ${read}`
  if (s.state === 'stale') return `Stale, last read ${read}`
  return `${s.partial ? 'Draft, read' : 'Read'} ${read}`
}

export const emailDomain = (email: string) => email.split('@')[1]?.toLowerCase() ?? ''

/** People with a card request started and not sent, oldest first. Internal and flagged requests are left out. */
export const stalled = (rows: Recent[]) =>
  rows.filter((r) => r.request_status === 'draft' && !r.staff_flag).sort((a, b) => (a.request_started_at ?? '').localeCompare(b.request_started_at ?? ''))

/** Requests sent since the start of yesterday, newest first. */
export const requestsSince = (rows: Recent[], now = new Date()) => {
  const from = yesterday(now)
  return rows.filter((r) => r.request_submitted_at && !r.staff_flag && dayOf(r.request_submitted_at) >= from)
    .sort((a, b) => (b.request_submitted_at ?? '').localeCompare(a.request_submitted_at ?? ''))
}

/** "www.linkedin.com" to "linkedin.com"; the row with no referrer gets a plain name. */
export const referrer = (name: string | null) => (!name || name.startsWith('(') ? 'No referrer' : name.replace(/^www\./, ''))

export function list(names: string[]): string {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}
